import React from "react";
import dayjs from "dayjs";
import { Tag, Avatar, Rate, Button, Empty } from "antd";
import { SendOutlined, PlusOutlined } from "@ant-design/icons";
import { getAgentColor, useQv4Theme } from "../../helpers";
import { useSuppliers } from "../../viewmodels/suppliers/useSuppliers";

export function SentSuppliersTab({ attempts, parts, onSendMore, userMap }) {
  const { t, isDark } = useQv4Theme();
  const { groups } = useSuppliers();

  if (!attempts?.length) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 0" }}>
        <Empty description="Not sent to suppliers yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        {onSendMore && <Button type="primary" icon={<SendOutlined />} onClick={onSendMore}>Send to Suppliers</Button>}
      </div>
    );
  }

  const quoteBySupplier = {};
  parts.forEach(p => {
    (p.quotes || []).forEach(q => {
      if (!quoteBySupplier[q.supplier_id]) quoteBySupplier[q.supplier_id] = { count: 0, partNames: [] };
      quoteBySupplier[q.supplier_id].count++;
      quoteBySupplier[q.supplier_id].partNames.push(p.part_name || `Part #${p.id}`);
    });
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {onSendMore && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button size="small" icon={<PlusOutlined />} onClick={onSendMore}>Send to more suppliers</Button>
        </div>
      )}
      {attempts.slice().reverse().map(attempt => (
        <div key={attempt.attempt}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Tag color="blue" style={{ margin: 0 }}>Attempt {attempt.attempt}</Tag>
            <span style={{ fontSize: 11, color: t.textSub }}>
              {attempt.sent_at ? dayjs(attempt.sent_at).format("DD MMM YYYY, hh:mm A") : "—"}
            </span>
            {attempt.sent_by && userMap?.[attempt.sent_by] && (
              <span style={{ fontSize: 11, color: t.textSub, fontWeight: 600 }}>
                · {userMap[attempt.sent_by]}
              </span>
            )}
            <div style={{ flex: 1 }} />
            <Tag style={{ margin: 0 }}>{attempt.suppliers?.length ?? 0} supplier{(attempt.suppliers?.length ?? 0) !== 1 ? "s" : ""}</Tag>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {(attempt.suppliers ?? []).map(s => {
              const q = quoteBySupplier[s.supplier_id];
              return (
                <div key={s.supplier_id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 10px", borderRadius: 6,
                  background: q ? (isDark ? "rgba(82,196,26,0.12)" : "#f6ffed") : t.headerBg,
                  border: `1px solid ${q ? "#b7eb8f" : t.border}`,
                }}>
                  <Avatar size={26} style={{ backgroundColor: getAgentColor(s.supplier_name), fontSize: 10, flexShrink: 0 }}>
                    {(s.supplier_name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.supplier_name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
                      {s.supplier_number && (
                        <span style={{ fontSize: 10, color: t.textSub }}>{s.supplier_number}</span>
                      )}
                      {s.supplier_score != null && (
                        <Rate disabled value={s.supplier_score} count={5} style={{ fontSize: 9 }} />
                      )}
                    </div>
                    {(() => {
                      const matchedGroupIds = s.matched_group_ids ?? [];
                      const matchedTags = groups.filter(g => matchedGroupIds.includes(g.value));
                      if (!matchedTags.length) return null;
                      return (
                        <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 3 }}>
                          {matchedTags.map(tg => (
                            <Tag key={tg.value} style={{ margin: 0, fontSize: 9, padding: "0 4px", lineHeight: "16px" }}>
                              {tg.label}
                            </Tag>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  {q
                    ? <Tag color="green" style={{ margin: 0, fontSize: 10, flexShrink: 0 }}>{q.count} quote{q.count !== 1 ? "s" : ""} received</Tag>
                    : <Tag color="orange" style={{ margin: 0, fontSize: 10, flexShrink: 0 }}>Awaiting</Tag>
                  }
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
